import { Button, ChevronDownIcon, Chip, FormControl, Heading, Input } from '@farol-ds/react'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { useIMask } from 'react-imask'

import {
  ENTITY_NOTICE_DIVIDER_TEXT,
  ENTITY_NOTICE_EMAIL_HELP_TEXT,
  ENTITY_NOTICE_EMAIL_LABEL,
  ENTITY_NOTICE_LEGAL_LINK_LABELS,
  ENTITY_NOTICE_LEGAL_TEXT_PREFIX,
  ENTITY_NOTICE_TITLE_TEMPLATE,
  ENTITY_NOTICE_WHATSAPP_LABEL,
  ENTITY_NOTICE_WHATSAPP_MASK_PLACEHOLDER,
  resolveEntityNoticeCopy,
} from '../../experiments/entity-notice-fakedoor'
import {
  clearEntityNoticePhone,
  saveEntityNoticePhone,
} from '../../utils/entity-notice-phone-storage'
import { ERROR_MESSAGES } from '../../utils/errorMessages'
import { AuthFooter } from '../auth-footer'
import { LoginCard } from '../login-card'
import { getPhoneDigits, isPhoneValid } from '../signup-cpf-form/signup-cpf-form'
import styles from './entity-notice-entry.module.scss'
import type { EntityNoticeEntryProps } from './entity-notice-entry.types'
import { EntityNoticeGoogleButton } from './entity-notice-google-button'
import { EntityNoticeTitle } from './entity-notice-title'
import { useEntityNoticeEmailContinue } from './use-entity-notice-email-continue'

export interface EntityNoticeEntryEmailFirstProps extends EntityNoticeEntryProps {
  variant: 'variantA' | 'variantC'
}

const CHIP_KIND_BY_VARIANT: Record<'variantA' | 'variantC', 'secondary' | undefined> = {
  variantA: undefined,
  variantC: 'secondary',
}

export function EntityNoticeEntryEmailFirst({
  variant,
  entityName,
  onSubmit,
  isLoading = false,
  error,
  onCloseClick,
  appId,
  featureCode,
  fromComponent,
  nextUrl,
}: EntityNoticeEntryEmailFirstProps) {
  const showWhatsApp = variant === 'variantA'
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [phoneError, setPhoneError] = useState(false)

  const {
    ref: whatsAppRef,
    unmaskedValue: whatsAppUnmasked,
    value: whatsAppValue,
  } = useIMask(useRef({ mask: '(00) 00000-0000', lazy: true }).current)

  useEffect(() => {
    clearEntityNoticePhone()
  }, [])

  useEffect(() => {
    if (!showWhatsApp) return
    const digits = getPhoneDigits(whatsAppUnmasked || '')
    if (isPhoneValid(digits)) {
      saveEntityNoticePhone(digits)
    }
  }, [showWhatsApp, whatsAppUnmasked])

  const handlePhoneBlur = () => {
    setPhoneTouched(true)
    setPhoneError(!isPhoneValid(getPhoneDigits(whatsAppUnmasked || '')))
  }

  const {
    isEmailVisible,
    emailValue,
    emailError,
    revealEmail,
    setEmailValue,
    clearEmailError,
    handleContinue: hookHandleContinue,
  } = useEntityNoticeEmailContinue({
    onSubmit,
    onBeforeValidate: () => {
      if (showWhatsApp) saveEntityNoticePhone(getPhoneDigits(whatsAppUnmasked || ''))
    },
  })

  const copy = resolveEntityNoticeCopy(variant, isEmailVisible ? 'show-email' : 'entry')

  const chipKind = CHIP_KIND_BY_VARIANT[variant]

  const handleContinue = () => {
    if (showWhatsApp) {
      const digits = getPhoneDigits(whatsAppUnmasked || '')
      if (!isPhoneValid(digits)) {
        setPhoneTouched(true)
        setPhoneError(true)
        return
      }
    }
    hookHandleContinue()
  }

  return (
    <LoginCard
      header={{
        align: 'center',
        title: (
          <EntityNoticeTitle
            template={ENTITY_NOTICE_TITLE_TEMPLATE}
            entityName={entityName || ''}
          />
        ),
      }}
      onCloseClick={onCloseClick}
    >
      <div className={styles.container}>
        {showWhatsApp && (
          <FormControl error={phoneTouched && phoneError}>
            <FormControl.Label>
              {ENTITY_NOTICE_WHATSAPP_LABEL}{' '}
              <span aria-hidden="true" className={styles.required}>
                *
              </span>
            </FormControl.Label>
            <Input
              ref={whatsAppRef}
              type="tel"
              name="whatsapp"
              inputMode="tel"
              required
              placeholder={ENTITY_NOTICE_WHATSAPP_MASK_PLACEHOLDER}
              value={whatsAppValue || ''}
              onBlur={handlePhoneBlur}
            />
            {phoneTouched && phoneError && (
              <FormControl.ErrorMessage>
                {ERROR_MESSAGES.signup.cpf.phone.invalid}
              </FormControl.ErrorMessage>
            )}
          </FormControl>
        )}

        <div className={styles.actionsGroup}>
          {copy.chipLabel && (
            <Chip
              size="sm"
              kind={chipKind}
              className={classNames(styles.chip, {
                [styles.chipBrandPrimary]: variant === 'variantA',
              })}
            >
              {copy.chipLabel}
            </Chip>
          )}

          <EntityNoticeGoogleButton
            kind={copy.googleButtonKind}
            appId={appId}
            featureCode={featureCode}
            fromComponent={fromComponent}
            nextUrl={nextUrl}
          />

          <div className={styles.divider}>
            <Heading size="sm" className={styles.dividerText}>
              {ENTITY_NOTICE_DIVIDER_TEXT}
            </Heading>
          </div>

          {isEmailVisible ? (
            <FormControl error={Boolean(emailError)}>
              <FormControl.Label>{ENTITY_NOTICE_EMAIL_LABEL}</FormControl.Label>
              <FormControl.Description>{ENTITY_NOTICE_EMAIL_HELP_TEXT}</FormControl.Description>
              <Input
                type="email"
                name="email"
                autoComplete="email"
                value={emailValue}
                onChange={(e) => {
                  setEmailValue(e.target.value)
                  if (emailError) clearEmailError()
                }}
              />
              {emailError && <FormControl.ErrorMessage>{emailError}</FormControl.ErrorMessage>}
            </FormControl>
          ) : (
            <button type="button" className={styles.emailLink} onClick={revealEmail}>
              {copy.emailLinkText}
              <ChevronDownIcon className={styles.emailLinkIcon} size="sm" />
            </button>
          )}

          {error && (
            <FormControl error>
              <FormControl.ErrorMessage>{error}</FormControl.ErrorMessage>
            </FormControl>
          )}

          {copy.ctaText && (
            <Button
              type="button"
              kind="primary"
              disabled={isLoading}
              loading={isLoading}
              onClick={handleContinue}
            >
              {copy.ctaText}
            </Button>
          )}
        </div>

        <AuthFooter
          textPrefix={ENTITY_NOTICE_LEGAL_TEXT_PREFIX}
          textComponent="body"
          linkLabels={ENTITY_NOTICE_LEGAL_LINK_LABELS}
          linkUnderline
          trailingPeriod
        />
      </div>
    </LoginCard>
  )
}
